import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { getPendingApprovalCount } from "../api/api";
import { useNotifications } from "../context/NotificationsContext";
import { TRADE_APPROVAL_CHANGED } from "../lib/tradeApprovalEvents";

const ATTENTION_SEVERITIES = new Set(["CRITICAL", "ERROR", "WARNING"]);

export default function DashboardActivityPanel({ bare = false }) {
  const navigate = useNavigate();
  const notifications = useNotifications() || {};
  const {
    items: notificationItems = [],
    loading: notificationsLoading = false,
    openDrawer = () => {},
  } = notifications;

  const [approvalCount, setApprovalCount] = useState(0);
  const [approvalsLoading, setApprovalsLoading] = useState(true);

  const fetchApprovalCount = useCallback(async () => {
    try {
      setApprovalsLoading(true);
      const data = await getPendingApprovalCount();
      setApprovalCount(typeof data === "number" ? data : data?.count || 0);
    } catch (error) {
      console.error("Failed to load dashboard approval count", error);
      setApprovalCount(0);
    } finally {
      setApprovalsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovalCount();
  }, [fetchApprovalCount]);

  useEffect(() => {
    const handleApprovalChange = () => {
      fetchApprovalCount();
    };

    window.addEventListener(TRADE_APPROVAL_CHANGED, handleApprovalChange);
    return () => window.removeEventListener(TRADE_APPROVAL_CHANGED, handleApprovalChange);
  }, [fetchApprovalCount]);

  const importantNotificationCount = useMemo(
    () => notificationItems.filter(
      (item) => !item?.read_at && item?.category !== "TRADE_APPROVAL" && (
        item?.category === "ALERT" ||
        item?.type === "alerts.triggered" ||
        ATTENTION_SEVERITIES.has(item?.severity)
      )
    ).length,
    [notificationItems]
  );

  const totalPendingCount = useMemo(
    () => approvalCount + importantNotificationCount,
    [approvalCount, importantNotificationCount]
  );

  const showLoader = totalPendingCount === 0 && (approvalsLoading || notificationsLoading);

  const ReviewContent = (
    <div className={bare ? "h-full min-h-[120px]" : "mt-4"}>
      {showLoader ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-borderDefault px-3 py-6 text-textSecondary">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : totalPendingCount === 0 ? (
        <div className="rounded-lg border border-dashed border-borderDefault px-3 py-4 text-sm text-textSecondary">
          No pending items.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-borderDefault divide-y divide-borderDefault">
          {approvalCount > 0 && (
            <button
              type="button"
              onClick={() => navigate("/approvals")}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-primary/5"
            >
              <div>
                <p className="text-sm font-medium text-textPrimary">Pending approvals</p>
                <p className="text-xs text-textSecondary">Trades waiting for a decision</p>
              </div>
              <span className="rounded-full bg-warning/15 px-2.5 py-1 text-xs font-medium text-warning">
                {approvalCount}
              </span>
            </button>
          )}

          {importantNotificationCount > 0 && (
            <button
              type="button"
              onClick={openDrawer}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-primary/5"
            >
              <div>
                <p className="text-sm font-medium text-textPrimary">Important alerts</p>
                <p className="text-xs text-textSecondary">Unread warnings, alerts, and errors</p>
              </div>
              <span className="rounded-full bg-destructive/15 px-2.5 py-1 text-xs font-medium text-destructive">
                {importantNotificationCount}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (bare) {
    return ReviewContent;
  }

  return (
    <aside className="bg-backgroundSurface rounded-xl p-4 sm:p-5 shadow-md">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-textSecondary">
        Pending Attention
      </p>
      <h3 className="mt-1 text-lg font-semibold text-primary">Needs review</h3>

      {ReviewContent}
    </aside>
  );
}
